/** A notice as it is written, cut into the part that names it and the rest. */
export interface PaloNoticeText {
    /** The opening line, where there is one short enough to read as a title. */
    title?: string;
    /** Everything else, as one flowing text. */
    body: string;
}

/** Longer than this, an opening line is a sentence and not a title. */
const TITLE_LENGTH = 60;

/**
 * A notice written over several lines, read as one line.
 *
 * Notices are maintained as little letters: a short line naming the subject,
 * then the matter, then whom to call. In the head of a document that costs
 * three lines of a bar that never scrolls — "Hinweise dürfen nie eingeklappt
 * werden" and the bar must still be small, so the breaks become "·" and the
 * naming line is set in bold (FRM-03, GF 12.10.2026).
 *
 * Where the first line is long enough to be the matter itself, nothing is
 * named and the whole notice simply flows.
 */
export const paloNoticeText = (text: string): PaloNoticeText => {
    const lines = text
        .split(/\r?\n/)
        .map(line => line.trim())
        .filter(Boolean);

    if (lines.length < 2 || lines[0].length > TITLE_LENGTH) {
        return { body: lines.join(' · ') };
    }

    // A title is set in bold here, so the emphasis a writer put around it is
    // taken off — it was written for a text that is read as it stands, and
    // would otherwise be printed as the asterisks it is made of.
    const [first, ...rest] = lines;
    const named = first
        .replace(/^\s*(\*\*|__|\*|_)(.*?)\1\s*$/, '$2')
        // Emphasis inside the line as well: "Bestellungen: **Anlagennummer**:"
        // was printed with its asterisks.
        .replace(/(\*\*|__)(.+?)\1/g, '$2')
        .replace(/\*(\S(?:.*?\S)?)\*/g, '$1')
        .trim();

    return {
        title: /[.:!?–-]$/.test(named) ? named : `${named}:`,
        body: rest.join(' · '),
    };
};
